import { useEffect, useState, useCallback } from "react";

import OAuthInfo from "@arcgis/core/identity/OAuthInfo.js";
import esriId from "@arcgis/core/identity/IdentityManager.js";
import Portal from "@arcgis/core/portal/Portal.js";
import config from "@arcgis/core/config.js";
import { AppResource } from '@config/config';
import { checkUserAuthStatus,revokeUserSession } from '@/libs/utils/appServerCall'; // <-- Import your shared helper

config.portalUrl = AppResource.ssoauth_url;

const oAuthInfo = new OAuthInfo({
  appId: AppResource.VITE_BOGS_AUTH_APPID || "FAKE_APP_ID_FOR_DEV",
  portalUrl: config.portalUrl,
  flowType: "auto",
  popup: false
});

esriId.registerOAuthInfos([oAuthInfo]);

export default function useBogsGisPortalAuth(isProductionFlow = true, { autoInit = true } = {}) {
  const [loading, setLoading] = useState(autoInit);
  const [signedIn, setSignedIn] = useState(false); 
  const [portalUser, setPortalUser] = useState(null);
  const [portalInstance, setPortalInstance] = useState(null); // <-- NEW: Store loaded Portal instance
  const [niogemsAuth, setNiogemsAuth] = useState(null);
  const [niogemsLoading, setNiogemsLoading] = useState(false); 
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    if (!autoInit) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function init() {
      setLoading(true);
      try {
        const sharingUrl = `${oAuthInfo.portalUrl}/sharing`;
        try {
          await esriId.checkSignInStatus(sharingUrl);
          setStatusMsg("Already signed in (Portal)");
        } catch {
          await esriId.getCredential(sharingUrl);
          setStatusMsg("Signed in via ArcGIS OAuth (Login.gov)");
        }
        
        if (cancelled) return;
        setSignedIn(true);

        const portal = new Portal({ url: oAuthInfo.portalUrl, authMode: "immediate" });
        await portal.load();

        if (cancelled) return;
        setPortalInstance(portal); // <-- Store portal object for reuse!

        const user = portal.user || null;
        if (!user) throw new Error("No active Portal user profile found.");

        const restUserUrl = user.url || `${portal.restUrl}/community/users/${user.username}`;
        const resp = await fetch(`${restUserUrl}?f=pjson`);
        if (!resp.ok) throw new Error(`User profile error: ${resp.status}`);
        const profile = await resp.json();

        const email = user.email || profile?.email || null;
        if (!email) throw new Error("Verified email is missing from Portal profile.");

        if (cancelled) return;

        setPortalUser({
          fullName: user.fullName,
          email,
          provider: profile?.provider || "unknown",
          userUrl: user.userUrl
        });

      } catch (err) {
        console.error("SSO/Auth error:", err);
        setStatusMsg("Portal authentication failed.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [autoInit]);

const fetchNiogemsToken = useCallback(async () => {
    if (!portalUser?.email) return;
    
    setNiogemsLoading(true);
    setStatusMsg("Checking system clearance rules...");
    try {
      const res = await checkUserAuthStatus(portalUser.email, isProductionFlow);
      
      // Handle immediate redirect instructions (Active state)
      if (res && res.action === "redirect") {
          window.location.href = res.url;
          return;
      }
      
      // Save data locally (Pending, Denied, NotExist states fall straight through here)
      setNiogemsAuth(res);
    } catch (err) {
      console.error("Handshake Exception:", err);
      setNiogemsAuth({
          action: "display",
          status: "NetworkError",
          message: "Failed to establish validation handshake with gateway proxy."
      });
    } finally {
      setNiogemsLoading(false);
    }
}, [portalUser, isProductionFlow]);

  const logout = useCallback(async () => {
    const userEmail = portalUser?.email;

    esriId.destroyCredentials();
    setPortalUser(null);
    setPortalInstance(null);
    setNiogemsAuth(null);
    setSignedIn(false);

    //if (window.localStorage) window.localStorage.removeItem("esriJSAPIOAuth");
    //if (window.sessionStorage) window.sessionStorage.removeItem("esriJSAPIOAuth");

    if (userEmail) {
      try {
       await revokeUserSession(userEmail);
      } catch (err) {
        console.error("Failed to revoke proxy session on server:", err);
      }
    }

    const portalSignoutUrl = `${oAuthInfo.portalUrl}/sharing/rest/oauth2/signout`;
    const targetHome = AppResource.redirectTarget;
    const clientId = oAuthInfo.appId;

    window.location.href = `${portalSignoutUrl}?client_id=${clientId}&redirect_uri=${encodeURIComponent(targetHome)}`;
  }, [portalUser]);

  return {
    loading,
    signedIn,
    portalUser,
    portal: portalInstance, // <-- EXPOSED: Cleanly reused downstream
    niogemsAuth,
    niogemsLoading,
    statusMsg,
    fetchNiogemsToken,
    logout 
  };
}